"use client";

import clsx from "clsx";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect } from "react";
import styles from "./tabBar.module.css";

interface ITab {
  name: string;
  label: string;
  disabled?: boolean;
  onClick?: () => void;
}

export interface ITabBarProps {
  tabs: ITab[];
  className?: string;
}

export const TabBar = ({ tabs, className }: ITabBarProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const activeTab = searchParams.get("tab");

  const updateActiveTab = useCallback(
    (tabName: string) => {
      router.replace(`${pathname}?tab=${tabName}`);
    },
    [pathname, router]
  );

  useEffect(() => {
    if (!activeTab) {
      updateActiveTab(tabs[0].name);
    }
  }, [activeTab, tabs, updateActiveTab]);

  return (
    <div className={clsx(styles.tabBar, className)}>
      {tabs
        .sort((a, b) => Number(a.disabled) - Number(b.disabled))
        .map(({ name, label, disabled, onClick }) => {
          const isActive = name === activeTab;

          const handleTabClick = () => {
            if (disabled) return;

            if (onClick) {
              onClick();
            } else {
              updateActiveTab(name);
            }
          };

          return (
            <div
              key={name}
              className={clsx(
                styles.tab,
                isActive && styles.tab__active,
                disabled && styles.tab__disabled
              )}
              onClick={handleTabClick}
            >
              {label}
            </div>
          );
        })}
    </div>
  );
};
